import { NotebookPen, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNotes } from '@/entities/note/hooks/use-notes';
import type { Note } from '@/entities/note/schemas/note';
import { RecordEditor } from '@/entities/shared/record/record-editor';
import { useEditorParam } from '@/shared/components/editor-sheet/use-editor-param';
import { EmptyState } from '@/shared/components/empty-state';
import { ListSkeleton } from '@/shared/components/list/list-skeleton';
import { Page } from '@/shared/components/page/page';
import { PageHeader } from '@/shared/components/page/page-header';
import { SearchInput } from '@/shared/components/search-input';
import { useTranslation } from '@/shared/i18n/use-translation';
import { matchesQuery } from '@/shared/lib/text/matches-query';
import { Button } from '@/shared/ui/button/button';
import { NoteCard } from './note-card';
import { NoteEditor } from './note-editor';

export function NotesScreen() {
  const { t } = useTranslation();
  const notes = useNotes();
  const editor = useEditorParam();
  const [query, setQuery] = useState('');
  const shown = useMemo(
    () =>
      (notes ?? [])
        .filter(({ data }) => matchesQuery(query, data.title, data.body, data.tags))
        .sort(
          (a, b) => Number(b.data.pinned) - Number(a.data.pinned) || b.updated_at.localeCompare(a.updated_at),
        ),
    [notes, query],
  );
  return (
    <Page>
      <PageHeader
        title={t('Notes')}
        description={t('Plans, emergency contacts, meeting points, procedures…')}
        primaryAction={{ label: t('New note'), onClick: () => editor.open('new') }}
      />
      {notes && notes.length > 0 && (
        <SearchInput placeholder={t('Search notes')} value={query} onChange={setQuery} />
      )}
      {!notes ? (
        <ListSkeleton />
      ) : notes.length === 0 ? (
        <EmptyState
          icon={NotebookPen}
          title={t('No notes')}
          description={t(
            'Write down the emergency plan, important phone numbers or anything you want at hand offline.',
          )}
          action={
            <Button onClick={() => editor.open('new')}>
              <Plus /> {t('Write a note')}
            </Button>
          }
        />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {shown.map((view) => (
            <NoteCard key={view.id} view={view} onOpen={() => editor.open(view.id)} />
          ))}
        </div>
      )}
      <RecordEditor<Note> collection="notes" id={editor.editing}>
        {(view) => <NoteEditor view={view} onClose={editor.close} />}
      </RecordEditor>
    </Page>
  );
}
